import { watch, nextTick } from "vue";
import model from "@/ts/model";
import type { SoarPayMethod, SoarPayPaymentReceiptData, SoarPayPaymentReceiptEventData, SoarPayPaymentType } from "@/ts/types/soar-pay-types";
import { removeArrayItem } from "@/ts/utils/utils";
import { navigateTo } from "@/ts/management/view-navigation-management";

type ObservedTrans = {
    transactionId: string,
    timestamp: number,
    thenDeposits: number,
    paymentType: SoarPayPaymentType,
};

const OBSERVED_TRANS_LIST_SUFFIX = 'observed_trans_list_';
const observedTransactions: ObservedTrans[] = [];

let observedTransCheckCount = 0;

export default {
    init(){
        window.addEventListener('storage', e => {
			if(e.key === 'close-payment-window' && model.paymentData.window){
				model.paymentData.window.close();
				model.paymentData.window = null;
                clearInterval(model.paymentData.interval);
				model.dispatch('ShowPaymentReceipt', {
                    detail: {
                        delayed: true,
                    } as SoarPayPaymentReceiptEventData,
                });
			}
		});

        const storedPaymentData = sessionStorage.getItem('payment-to-display-after-redirect');
        if(storedPaymentData){
            sessionStorage.removeItem('payment-to-display-after-redirect');
            let data = JSON.parse(storedPaymentData) as SoarPayPaymentReceiptData;

            const unwatch = watch(() => model.userLoggedIn, async value => {
                if(!value) return;

                await nextTick();
                unwatch();

                const methodId = data.methodId;
                if(!methodId) return;

                const paymentType = data.paymentType;
                if(!paymentType) return;

                await navigateTo(paymentType == 'deposit' ? 'deposit-standalone' : 'withdrawal');
                await nextTick();

                if(!model.userLoggedIn) return;

                const methods = model.soarPay.methods?.[paymentType]?.methods;
                if(!methods) return;

                const method = methods.find(x => x.id == methodId);
                if(!method) return;

                data.method = method;
                delete data.methodId;

                model.paymentReceiptData = data;
                model.dispatch('OpenMethodAndReceipt');
            }, {immediate: true});
        }

        setupObserveSuccessfulTransactions();
    },
}

function setupObserveSuccessfulTransactions(){
    if(!model.observeSuccessfulTrans) return;

    let timeout = 0;
    let resetAmount = 0; 

    watch(() => model.userLoggedIn, value => {
        clearTimeout(timeout);

        if(!model.userId || !value){
            resetAmount++;
            while(observedTransactions.length > 0) observedTransactions.pop();
            observedTransCheckCount = 0;
            return;
        }

        let observedList: ObservedTrans[] | string | null = localStorage.getItem(OBSERVED_TRANS_LIST_SUFFIX + model.userId);
        if(!observedList) return;

        try{
            observedList = JSON.parse(observedList) as ObservedTrans[];
            observedList.forEach(x => observedTransactions.push(x));
        }
        catch(e){
            console.warn(e);
        }

        resetAmount++;
        checkTransItems(resetAmount);
    }, {immediate: true});

    model.on('ResetObservedTransCheck', () => {
        observedTransCheckCount = 0;
        clearTimeout(timeout);
        resetAmount++;
        checkTransItems(resetAmount);
    });

    async function checkTransItems(currResetAmount: number){
        clearTimeout(timeout);
        if(!model.userLoggedIn) return;

        const now = Date.now();

        if(model.isTest) console.log('check observed transactions');

        for(let i = observedTransactions.length - 1; i >= 0; i--){
            const item = observedTransactions[i];

            if(now - item.timestamp > 1000 * 60 * 60 * 24 * 3){
                removeArrayItem(item, observedTransactions);
                continue;
            }

            const soarPayService = (await import("@/ts/services/soar-pay-service")).default;
            if(currResetAmount != resetAmount) return;

            if(!model.userLoggedIn) break;

            const trans = await soarPayService.getTransactionStatus(item.transactionId, false);
            if(currResetAmount != resetAmount) return;

            if(!model.userLoggedIn) break;

            if(!trans || (trans.txState == 'CANCELLED' || trans.txState == 'FAILED' || trans.txState == 'INCONSISTENT')){
                removeArrayItem(item, observedTransactions);
                continue;
            }

            if(trans.statusCode == 'SUCCESS'){
                const dataLayerObj: Record<string, string | number> = {
                    'event': item.paymentType == 'deposit' ? (item.thenDeposits == 0 ? 'purchase_first' : 'purchase') : 'withdraw',
                    'txid': trans.txId || item.transactionId,
                    'tamt': (trans.payAmount || '').replace(/[^0-9.]/g, '').trim(),
                    'tcur': (trans.payAmount || '').replaceAll(/\d./g, '').replaceAll('.', '').trim(),
                    'tpsp': 0,
                }

                if(model.isTest) {
                    console.log('Payment data layer object: ');
                    console.log(dataLayerObj);
                }

                window.dataLayer?.push(dataLayerObj);

                removeArrayItem(item, observedTransactions);
                observedTransactions.forEach(x => x.thenDeposits++);
                continue;
            }
        }

        if(!model.userLoggedIn) {
            clearTimeout(timeout);
            return;
        }

        updateObservedSuccessfulTransStorage();

        observedTransCheckCount++;

        clearTimeout(timeout);
        timeout = setTimeout(() => checkTransItems(resetAmount), observedTransCheckCount < 10 ? 5000 : (1000 * 60 * 10)); 
    };
}

function updateObservedSuccessfulTransStorage(){
    if(!model.userId || !model.userLoggedIn) return;
    localStorage.setItem(OBSERVED_TRANS_LIST_SUFFIX + model.userId, JSON.stringify(observedTransactions));
}

export function addObservedTrans(transactionId: string, method: SoarPayMethod){
    if(model.observeSuccessfulTrans && transactionId){
        if(!observedTransactions.some(x => x.transactionId == transactionId)){
            observedTransactions.push({
                transactionId: transactionId,
                timestamp: Date.now(),
                thenDeposits: model.playerInfo?.paymentMethods?.deposits || 0,
                paymentType: method.paymentType || 'deposit',
            });
            updateObservedSuccessfulTransStorage();
        }
    }
}